import { useEffect, useState } from 'react';
import { officeReady } from '@/services/office/officeReady';
import {
  currentOfficeTheme,
  prefersDarkMode,
  resolveThemeMode,
  type OfficeThemeLike,
  type ThemeMode,
} from '@/services/office/theme';

// Minimal shape of the bits of Office.context.mailbox we touch, kept loose so
// this compiles regardless of the installed @types/office-js version and
// degrades gracefully on hosts that predate the OfficeThemeChanged event.
interface ThemeAwareMailbox {
  addHandlerAsync?: (
    eventType: unknown,
    handler: (arg: unknown) => void,
    callback?: (result: unknown) => void,
  ) => void;
  removeHandlerAsync?: (
    eventType: unknown,
    options: unknown,
    callback?: (result: unknown) => void,
  ) => void;
}

/**
 * The task pane's active theme, following the Outlook host theme and reacting
 * to live theme switches. Starts from the OS preference (synchronous, avoids a
 * first-paint flash), then refines from Office.context.officeTheme once Office
 * is ready.
 */
export function useThemeMode(): ThemeMode {
  const [mode, setMode] = useState<ThemeMode>(() =>
    prefersDarkMode() ? 'dark' : 'light',
  );

  useEffect(() => {
    let cancelled = false;
    const apply = (theme?: OfficeThemeLike | null) => {
      if (!cancelled) setMode(resolveThemeMode(theme));
    };

    // Fallback path + hosts that reflect the host theme through the OS setting.
    const mq =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;
    const onMq = () => apply();
    mq?.addEventListener?.('change', onMq);

    let removeOfficeHandler: (() => void) | undefined;
    void officeReady().then(() => {
      apply(); // officeTheme is readable now

      const office = (globalThis as { Office?: typeof Office }).Office;
      const mailbox = office?.context?.mailbox as unknown as ThemeAwareMailbox | undefined;
      const evt = (office?.EventType as Record<string, unknown> | undefined)?.OfficeThemeChanged;
      if (mailbox?.addHandlerAsync && evt) {
        const handler = (arg: unknown) => {
          const theme = (arg as { officeTheme?: OfficeThemeLike } | undefined)?.officeTheme;
          apply(theme ?? currentOfficeTheme());
        };
        try {
          mailbox.addHandlerAsync(evt, handler);
          removeOfficeHandler = () => {
            try {
              mailbox.removeHandlerAsync?.(evt, { handler });
            } catch {
              /* best-effort */
            }
          };
        } catch {
          /* theme-change subscription is best-effort */
        }
      }
    });

    return () => {
      cancelled = true;
      mq?.removeEventListener?.('change', onMq);
      removeOfficeHandler?.();
    };
  }, []);

  return mode;
}
